import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListUl } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@progress/kendo-react-buttons';
import useChartStore from '../../../../../../../Zustand/chartStore';
import { useAnomalyBandContext } from './AnomalyBand/AnomalyBandContext';
import type { AnomalyBandWithForm } from './AnomalyBand/types';
import { DEFAULT_ANOMALY_FORM_VALUES } from './AnomalyBand/types';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../../../../messages/en-US';

interface Props {
  endpointId: string;
  eventFilter: { startDate: Date; endDate: Date };
  userId?: string;
  userName?: string;
  legendVisible?: boolean;
  onLegendToggle?: () => void;
  disabled?: boolean;
}

/** Prefer the chart X-axis visible window (zoom); fall back to the full date filter range. */
function getPlacementTimeRangeSec(
  eventFilter: { startDate: Date; endDate: Date },
  visibleRange: { min: number | undefined; max: number | undefined }
): { startTime: number; endTime: number } {
  const filterStart = Math.floor(eventFilter.startDate.getTime() / 1000);
  const filterEnd = Math.floor(eventFilter.endDate.getTime() / 1000);

  const min = visibleRange.min;
  const max = visibleRange.max;

  if (min != null && max != null && Number.isFinite(min) && Number.isFinite(max)) {
    const vMin = Math.min(min, max);
    const vMax = Math.max(min, max);
    const placementStart = Math.max(filterStart, vMin);
    const placementEnd = Math.min(filterEnd, vMax);
    if (placementEnd > placementStart) {
      return { startTime: placementStart, endTime: placementEnd };
    }
  }

  return { startTime: filterStart, endTime: filterEnd };
}

/** Share of the visible/placement time window — maps to the same share of chart width on screen. */
const NEW_BAND_WIDTH_PERCENT = 10;
/** Smallest band (when the window is wide enough); avoids invisible slivers when zoomed in. */
const MIN_BAND_WIDTH_SEC = 5 * 60;

function computeBandWidthSec(placementSpanSec: number): number {
  if (placementSpanSec <= 0) {
    return 60;
  }

  const desired = Math.floor((placementSpanSec * NEW_BAND_WIDTH_PERCENT) / 100);
  const minTarget = Math.min(MIN_BAND_WIDTH_SEC, placementSpanSec);
  let width = Math.max(desired, minTarget);
  width = Math.min(width, placementSpanSec);

  return Math.max(1, width);
}

export default function AnomalyDetectorButtons({
  endpointId,
  eventFilter,
  userId,
  userName,
  legendVisible,
  onLegendToggle,
  disabled = false,
}: Props): React.ReactElement | null {
  const ctx = useAnomalyBandContext();
  const [bandCounter, setBandCounter] = useState(1);
  const localizationService = useLocalization();
  const visibleRange = useChartStore((state) => state.visibleRange);
  if (!ctx) return null;

  const { bands, setBands, selectedBandId, setSelectedBandId, setPendingDeleteBandId } = ctx;

  const createBand = (bandType: AnomalyBandWithForm['bandType']) => {
    const { startTime, endTime } = getPlacementTimeRangeSec(eventFilter, visibleRange);
    const placementSpanSec = endTime - startTime;
    const bandWidthSec = computeBandWidthSec(placementSpanSec);
    const midpoint = Math.floor((startTime + endTime) / 2);
    const halfWidth = Math.floor(bandWidthSec / 2);
    let bandStart = midpoint - halfWidth;
    let bandEnd = midpoint + halfWidth;

    if (bandStart < startTime) {
      bandStart = startTime;
      bandEnd = Math.min(startTime + bandWidthSec, endTime);
    }

    if (bandEnd > endTime) {
      bandEnd = endTime;
      bandStart = Math.max(endTime - bandWidthSec, startTime);
    }

    const newBand: AnomalyBandWithForm = {
      id: `band-${bandCounter}`,
      bandType,
      anomalyId: undefined,
      startTime: bandStart,
      endTime: bandEnd,
      ...DEFAULT_ANOMALY_FORM_VALUES,
      createdByUserId: userId,
      createdByUserName: userName,
    };

    setBandCounter((c) => c + 1);
    setBands((prev) => [...prev, newBand]);
    setSelectedBandId(newBand.id);
  };

  const handleAddBand = () => {
    createBand('anomaly');
  };

  const handleAddAnchor = () => {
    createBand('anchor');
  };

  const handleDeleteSelected = async () => {
    if (!selectedBandId) return;
    const selectedBand = bands.find((band) => band.id === selectedBandId);

    if (!selectedBand) return;

    if (selectedBand.persistedId) {
      setPendingDeleteBandId(selectedBandId);
      return;
    }

    setBands((prev) => prev.filter((b) => b.id !== selectedBandId));
    setSelectedBandId(null);
    setPendingDeleteBandId(null);
  };

  return (
    <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={handleAddBand} disabled={disabled}>
          {localizationService.toLanguageString('custom.addAnomaly', enMessages.custom.addAnomaly)}
        </Button>
        <Button onClick={handleAddAnchor} disabled={disabled}>
          {localizationService.toLanguageString('custom.addAnchor', enMessages.custom.addAnchor)}
        </Button>
        <Button onClick={handleDeleteSelected} disabled={disabled || !selectedBandId}>
          {localizationService.toLanguageString('custom.deleteSelected', enMessages.custom.deleteSelected)}
        </Button>
        {onLegendToggle && (
          <Button
            togglable
            selected={legendVisible}
            onClick={onLegendToggle}
            disabled={disabled}
            title={localizationService.toLanguageString('custom.legend', enMessages.custom.legend)}
          >
            <FontAwesomeIcon icon={faListUl} />
          </Button>
        )}
      </div>
    </div>
  );
}
